"use client";
import React, { useId, useState, useEffect } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import type { Container } from "@tsparticles/engine";
import { loadSlim } from "@tsparticles/slim";
import { cn } from "@/lib/utils";
import { motion, useAnimation } from "framer-motion";

type ParticlesProps = {
  className?: string;
  particleColor?: string;
  number?: {
    density: {
      enable: boolean;
      width: number;
      height: number;
    };
    value: number;
  };
  speed?: number;
  minSize?: number;
  maxSize?: number;
  background?: string;
  particleDensity?: number;
};

export const SparklesCore = (props: ParticlesProps) => {
  const { 
    className, 
    particleColor, 
    number, 
    speed = 1, 
    minSize = 0.6, 
    maxSize = 1.4, 
    background = "transparent",
    particleDensity = 100
  } = props;
  
  const [init, setInit] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const controls = useAnimation();
  const generatedId = useId();

  const particlesLoaded = async (container?: Container) => {
    if (container) {
      controls.start({
        opacity: 1,
        transition: {
          duration: 1,
        },
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  return (
    <motion.div 
      animate={controls} 
      className={cn("opacity-0", className)}
      onMouseMove={handleMouseMove}
    >
      {init && (
        <Particles
          className={cn("h-full w-full")}
          particlesLoaded={particlesLoaded}
          options={{
            background: {
              color: {
                value: background,
              },
            },
            fullScreen: {
              enable: false,
              zIndex: 1,
            },
            particles: {
              color: {
                value: particleColor || [
                  "#22c55e", // Green
                  "#ef4444", // Red
                ],
              },
              move: {
                enable: true,
                speed: { min: speed / 2, max: speed },
                direction: "none",
                random: true,
                straight: false,
                outModes: "out",
                attract: {
                  enable: true,
                  distance: 100,
                  rotate: {
                    x: 600,
                    y: 600,
                  },
                },
              },
              number: number || {
                density: {
                  enable: true,
                  width: 800,
                  height: 800
                },
                value: particleDensity,
              },
              opacity: {
                value: { min: 0.3, max: 0.8 },
                animation: {
                  enable: true,
                  speed: 1,
                },
              },
              size: {
                value: { min: minSize, max: maxSize },
              },
              interactivity: {
                events: {
                  onHover: {
                    enable: true,
                    mode: "grab",
                  },
                },
                modes: {
                  grab: {
                    distance: 140,
                    links: {
                      opacity: 0.5,
                    },
                  },
                },
              },
            },
            detectRetina: true,
          }}
        />
      )}
    </motion.div>
  );
};